import SpecificHand from '@/services/hands/SpecificHand.js';
import utilities from '@/services/utilities/utilities';
import _ from 'lodash';

export default class GeneralHand {
  constructor(generalHandString) {
    this.string = generalHandString;
    this.rankOne = generalHandString[0];
    this.rankTwo = generalHandString[1];
    this.displayString = this._getDisplayString();
    this.suitedOffsuitOrPair = this._getSuitedOffsuitOrPair();
    this.specificHands = this._getSpecificHands();
    this.matchesFiveCardHandHover = false;
    this.equity = null // for equity matrix
    this.color = null; // for range constructor
  };

  // constructor helpers
  _getDisplayString() {
    let displayString = this.rankOne.toUpperCase() + this.rankTwo.toUpperCase();

    if (this.string[2]) {
      displayString += this.string[2];
    }

    return displayString;
  };
  _getSuitedOffsuitOrPair() {
    if (this.string[2] === 's') {
      return 'suited';
    } else if (this.string[2] === 'o') {
      return 'offsuit';
    } else {
      return 'pair';
    }
  };
  _getSpecificHands(generalHandString) {
    if (this.suitedOffsuitOrPair === 'suited') {
      return this.__getSpecificSuitedHands();
    } else if (this.suitedOffsuitOrPair === 'offsuit') {
      return this.__getSpecificOffsuitHands();
    } else if (this.suitedOffsuitOrPair === 'pair') {
      return this.__getSpecificPairHands();
    }
  };
  __getSpecificSuitedHands() {
    let specificSuitedHands = {
      cc: new SpecificHand(this.rankOne + 'c' + this.rankTwo + 'c'),
      ss: new SpecificHand(this.rankOne + 's' + this.rankTwo + 's'),
      hh: new SpecificHand(this.rankOne + 'h' + this.rankTwo + 'h'),
      dd: new SpecificHand(this.rankOne + 'd' + this.rankTwo + 'd'),
    };

    specificSuitedHands.cc.generalHand = this;
    specificSuitedHands.ss.generalHand = this;
    specificSuitedHands.hh.generalHand = this;
    specificSuitedHands.dd.generalHand = this;

    return specificSuitedHands;
  };
  __getSpecificOffsuitHands() {
    let specificOffsuitHands = {
      cs: new SpecificHand(this.rankOne + 'c' + this.rankTwo + 's'),
      ch: new SpecificHand(this.rankOne + 'c' + this.rankTwo + 'h'),
      cd: new SpecificHand(this.rankOne + 'c' + this.rankTwo + 'd'),
      sc: new SpecificHand(this.rankOne + 's' + this.rankTwo + 'c'),
      sh: new SpecificHand(this.rankOne + 's' + this.rankTwo + 'h'),
      sd: new SpecificHand(this.rankOne + 's' + this.rankTwo + 'd'),
      hc: new SpecificHand(this.rankOne + 'h' + this.rankTwo + 'c'),
      hs: new SpecificHand(this.rankOne + 'h' + this.rankTwo + 's'),
      hd: new SpecificHand(this.rankOne + 'h' + this.rankTwo + 'd'),
      dc: new SpecificHand(this.rankOne + 'd' + this.rankTwo + 'c'),
      ds: new SpecificHand(this.rankOne + 'd' + this.rankTwo + 's'),
      dh: new SpecificHand(this.rankOne + 'd' + this.rankTwo + 'h'),
    };

    specificOffsuitHands.cs.generalHand = this;
    specificOffsuitHands.ch.generalHand = this;
    specificOffsuitHands.cd.generalHand = this;
    specificOffsuitHands.sc.generalHand = this;
    specificOffsuitHands.sh.generalHand = this;
    specificOffsuitHands.sd.generalHand = this;
    specificOffsuitHands.hc.generalHand = this;
    specificOffsuitHands.hs.generalHand = this;
    specificOffsuitHands.hd.generalHand = this;
    specificOffsuitHands.dc.generalHand = this;
    specificOffsuitHands.ds.generalHand = this;
    specificOffsuitHands.dh.generalHand = this;

    return specificOffsuitHands;
  };
  __getSpecificPairHands() {
    let specificPairHands = {
      cs: new SpecificHand(this.rankOne + 'c' + this.rankTwo + 's'),
      ch: new SpecificHand(this.rankOne + 'c' + this.rankTwo + 'h'),
      cd: new SpecificHand(this.rankOne + 'c' + this.rankTwo + 'd'),
      sh: new SpecificHand(this.rankOne + 's' + this.rankTwo + 'h'),
      sd: new SpecificHand(this.rankOne + 's' + this.rankTwo + 'd'),
      hd: new SpecificHand(this.rankOne + 'h' + this.rankTwo + 'd'),
    };

    specificPairHands.cs.generalHand = this;
    specificPairHands.ch.generalHand = this;
    specificPairHands.cd.generalHand = this;
    specificPairHands.sh.generalHand = this;
    specificPairHands.sd.generalHand = this;
    specificPairHands.hd.generalHand = this;

    return specificPairHands;
  };

  // getters
  get possible() {
    let possible = false;

    this.forEachSpecificHand(function (specificHand) {
      if (specificHand.possible) {
        possible = true;
      }
    });

    return possible;
  };
  get numPossibleSpecificHands() {
    let numPossibleSpecificHands = 0;

    this.forEachSpecificHand(function (specificHand) {
      if (specificHand.possible) {
        numPossibleSpecificHands++;
      }
    });

    return numPossibleSpecificHands;
  };
  get numPossibleSpecificHandsFromSuitedOffsuitOrPair() {
    if (this.suitedOffsuitOrPair === 'suited') {
      return 4;
    } else if (this.suitedOffsuitOrPair === 'offsuit') {
      return 12;
    } else if (this.suitedOffsuitOrPair === 'pair') {
      return 6;
    }
  };

  get inRange() {
    if (this.numSpecificHandsInRange === 0) {
      return false;
    } else if (this.numSpecificHandsInRange < this.numPossibleSpecificHands) {
      return 'partial';
    } else if (this.numSpecificHandsInRange === this.numPossibleSpecificHands) {
      return true;
    } else {
      throw new Error('Problem determining whether general hand is in range.');
    }
  };
  get numSpecificHandsInRange() {
    let numSpecificHandsInRange = 0;

    this.forEachPossibleSpecificHand(function (specificHand) {
      if (specificHand.inRange) {
        numSpecificHandsInRange++;
      }
    });

    return numSpecificHandsInRange;
  };

  get exactWeight() {
    let uniqueWeightsOfSpecificHandsInRange = [];

    this.forEachSpecificHandInRange(function (specificHand) {
      if (!uniqueWeightsOfSpecificHandsInRange.includes(specificHand.weight)) {
        uniqueWeightsOfSpecificHandsInRange.push(specificHand.weight);
      }
    });

    if (uniqueWeightsOfSpecificHandsInRange.length === 1) {
      return uniqueWeightsOfSpecificHandsInRange[0];
    } else {
      return null;
    }
  };
  get averageWeight() {
    let weightsOfSpecificHandsInRange = [];
    let averageWeight;

    if (this.numSpecificHandsInRange === 0) {
      throw new Error('Trying to get the average weight of a general hand that has no specific hands in range.');
    }

    this.forEachSpecificHandInRange(function (specificHand) {
      weightsOfSpecificHandsInRange.push(specificHand.weight);
    });

    averageWeight = _.mean(weightsOfSpecificHandsInRange).roundToNearestFive();

    return averageWeight;
  };
  get maxPossibleWeight() {
    if (this.numPossibleSpecificHands === 0) {
      return 0;
    }

    let maxPossibleWeight = 100;

    this.forEachPossibleSpecificHand(function (specificHand) {
      if (specificHand.maxPossibleWeight < maxPossibleWeight) {
        maxPossibleWeight = specificHand.maxPossibleWeight;
      }
    });

    return maxPossibleWeight;
  };

  get manuallySet() {
    if (this.numSpecificHandsManuallySet === 0) {
      return false;
    } else if (this.numSpecificHandsManuallySet < this.numPossibleSpecificHands) {
      return 'partial';
    } else if (this.numSpecificHandsManuallySet === this.numPossibleSpecificHands) {
      return true;
    } else {
      throw new Error('Problem determining whether general hand is manually set.');
    }
  };
  get manuallyAdded() {
    if (this.numSpecificHandsManuallyAdded === 0) {
      return false;
    } else if (this.numSpecificHandsManuallyAdded < this.numPossibleSpecificHands) {
      return 'partial';
    } else if (this.numSpecificHandsManuallyAdded === this.numPossibleSpecificHands) {
      return true;
    } else {
      throw new Error('Problem determining whether general hand is manually added.');
    }
  };
  get manuallyRemoved() {
    if (this.numSpecificHandsManuallyRemoved === 0) {
      return false;
    } else if (this.numSpecificHandsManuallyRemoved < this.numPossibleSpecificHands) {
      return 'partial';
    } else if (this.numSpecificHandsManuallyRemoved === this.numPossibleSpecificHands) {
      return true;
    } else {
      throw new Error('Problem determining whether general hand is manually removed.');
    }
  };
  get numSpecificHandsManuallySet() {
    let numSpecificHandsManuallySet = 0;

    this.forEachPossibleSpecificHand(function (specificHand) {
      if (specificHand.manuallySet) {
        numSpecificHandsManuallySet++;
      }
    });

    return numSpecificHandsManuallySet;
  };
  get numSpecificHandsManuallyAdded() {
    let numSpecificHandsManuallyAdded = 0;

    this.forEachPossibleSpecificHand(function (specificHand) {
      if (specificHand.manuallyAdded) {
        numSpecificHandsManuallyAdded++;
      }
    });

    return numSpecificHandsManuallyAdded;
  };
  get numSpecificHandsManuallyRemoved() {
    let numSpecificHandsManuallyRemoved = 0;

    this.forEachPossibleSpecificHand(function (specificHand) {
      if (specificHand.manuallyRemoved) {
        numSpecificHandsManuallyRemoved++;
      }
    });

    return numSpecificHandsManuallyRemoved;
  };

  get spaceBetweenRanks() {
    return utilities.ranks.getSpaceBetweenRanks([this.rankOne, this.rankTwo]);
  };

  // setters
  set maxPossibleWeight(value) {
    this.forEachSpecificHand(function (specificHand) {
      specificHand.maxPossibleWeight = value;
    });
  }
  set weight(newWeight) {
    this.forEachSpecificHand(function (specificHand) {
      specificHand.weight = newWeight;
    });
  };

  // iterators
  forEachSpecificHand(cb, thisValue) {
    let returnValueOfCurrIteration;

    for (let specificHandSuits in this.specificHands) {
      returnValueOfCurrIteration = cb.call(thisValue, this.specificHands[specificHandSuits], specificHandSuits);

      if (returnValueOfCurrIteration === 'break') {
        break;
      }
    }
  };
  forEachPossibleSpecificHand(cb, thisValue) {
    this.forEachSpecificHand(function (specificHand, specificHandSuits) {
      if (specificHand.possible) {
        return cb.call(thisValue, specificHand, specificHandSuits);
      }
    });
  };
  forEachSpecificHandInRange(cb, thisValue) {
    this.forEachSpecificHand(function (specificHand) {
      if (specificHand.possible && specificHand.inRange) {
        return cb.call(thisValue, specificHand);
      }
    });
  }

  // other
  getSpecificHandStringsToSelect(combosToAdd) {
    let specificHandStringsToSelect = [];

    this.forEachPossibleSpecificHand(function (specificHand) {
      if (specificHand.availableToSelect(combosToAdd)) {
        specificHandStringsToSelect.push(specificHand.string);
      }
    });

    return specificHandStringsToSelect;
  };
  highlightIfMatchesSpecificHandStringList(specificHandStrings) {
    let matchesSpecificHandStringList = this._getWhetherMatchesSpecificHandStringsList(specificHandStrings);

    if (matchesSpecificHandStringList) {
      this.matchesFiveCardHandHover = true;
    }
  };
  _getWhetherMatchesSpecificHandStringsList(specificHandStrings) {
    let matchesSpecificHandStringList = false;

    this.forEachSpecificHand(function (specificHand) {
      if (specificHandStrings.includes(specificHand.string)) {
        matchesSpecificHandStringList = true;
        return 'break';
      }
    });

    return matchesSpecificHandStringList;
  }
};



// WEBPACK FOOTER //
// ./src/services/hands/GeneralHand.js